import React, { useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Mail, ArrowRight } from "lucide-react";

// Public Brevo form endpoint (client-side POST — Brevo handles double opt-in,
// CAPTCHA, confirmation email and adds the confirmed subscriber to the
// "People Watching — Watch List" list). No API key or secret is used.
const BREVO_FORM_URL = process.env.REACT_APP_BREVO_FORM_URL;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const EmailCapture = () => {
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(true);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();

    const trimmed = email.trim();
    if (!trimmed || !EMAIL_RE.test(trimmed)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    if (!consent) {
      toast.error("Please tick the box to join the Watch List.");
      return;
    }
    if (!BREVO_FORM_URL) {
      toast.error("Signup temporarily unavailable. Please try again shortly.");
      return;
    }

    setLoading(true);
    try {
      // Brevo expects the same fields as the embed HTML:
      //   EMAIL, OPT_IN=1, email_address_check (honeypot), locale=en
      const body = new FormData();
      body.append("EMAIL", trimmed);
      body.append("OPT_IN", "1");
      body.append("email_address_check", "");
      body.append("locale", "en");

      // Brevo does not return CORS headers on its public form endpoint,
      // so we submit no-cors. The submission still reaches Brevo, which
      // dispatches the double opt-in confirmation email.
      await fetch(BREVO_FORM_URL, {
        method: "POST",
        mode: "no-cors",
        body,
      });

      toast.success(
        "Nearly there — check your inbox to confirm your place on the Watch List. 👀"
      );
      setEmail("");
      setConsent(true);
    } catch {
      toast.error("Something went wrong. Please try again.");
    }
    setLoading(false);
  };

  return (
    <section
      id="watch-list"
      data-testid="email-capture-section"
      className="py-10 md:py-28 relative overflow-hidden"
      style={{ backgroundColor: "var(--pw-yellow)" }}
    >
      <div className="absolute inset-0 pointer-events-none opacity-40">
        {[
          { l: "8%",  t: "20%", c: "var(--pw-navy)" },
          { l: "18%", t: "75%", c: "var(--pw-orange)" },
          { l: "82%", t: "18%", c: "var(--pw-orange)" },
          { l: "90%", t: "70%", c: "var(--pw-navy)" },
          { l: "44%", t: "8%",  c: "var(--pw-pink)" },
          { l: "56%", t: "88%", c: "var(--pw-purple)" },
        ].map((d, i) => (
          <span
            key={i}
            className="absolute w-3 h-3 rounded-full"
            style={{ left: d.l, top: d.t, backgroundColor: d.c }}
          />
        ))}
      </div>

      <div className="max-w-4xl mx-auto px-5 md:px-8 text-center relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <p
            className="eyebrow mb-5"
            style={{ color: "var(--pw-navy)" }}
          >
            Watch List · No spam. Ever.
          </p>
          <h2
            className="font-display font-black leading-[0.95] tracking-tighter"
            style={{ fontSize: "clamp(2.25rem, 6vw, 5rem)", color: "var(--pw-navy)" }}
          >
            Join the Watch List.
          </h2>
          <p
            className="mt-5 text-lg max-w-xl mx-auto"
            style={{ color: "rgba(14,26,43,0.75)" }}
          >
            Be first to know when People Watching launches, get early access to the first
            production run and receive the occasional bit of People Watching nonsense.
          </p>

          <form
            onSubmit={submit}
            data-testid="email-capture-form"
            className="mt-9 flex flex-col sm:flex-row gap-3 max-w-lg mx-auto"
            noValidate
          >
            <div className="flex-1 relative">
              <Mail
                className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4"
                style={{ color: "rgba(14,26,43,0.55)" }}
              />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                data-testid="email-capture-input"
                aria-label="Email address"
                autoComplete="email"
                className="w-full pl-11 pr-5 py-4 rounded-full bg-white border border-[color:var(--pw-navy)]/10 text-[color:var(--pw-navy)] placeholder:text-[color:var(--pw-navy)]/50 focus:outline-none focus:ring-2 focus:ring-[color:var(--pw-navy)]"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="email-capture-submit"
              className="inline-flex items-center justify-center gap-2 rounded-full px-7 py-4 text-sm font-semibold uppercase tracking-wider transition-all hover:scale-[1.02] disabled:opacity-70 disabled:cursor-not-allowed"
              style={{ backgroundColor: "var(--pw-navy)", color: "#fff" }}
            >
              {loading ? "Adding…" : (
                <>
                  Join Watch List <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </form>

          <label
            className="mt-4 inline-flex items-start gap-3 text-xs md:text-sm max-w-lg mx-auto text-left cursor-pointer py-2"
            style={{ color: "rgba(14,26,43,0.7)", minHeight: "44px" }}
          >
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              data-testid="email-capture-consent"
              className="w-5 h-5 mt-0.5 accent-[color:var(--pw-navy)] cursor-pointer flex-shrink-0"
            />
            <span className="pt-0.5">
              I&rsquo;d like to receive email updates from People Watching. I can unsubscribe at any time.
            </span>
          </label>
        </motion.div>
      </div>
    </section>
  );
};

export default EmailCapture;
